import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { requisicionesService, inventarioService } from '../services/api';
import { imprimirRequisicion } from '../utils/pdfRequisicion';
import ModalLlaveAcceso from '../components/ModalLlaveAcceso';
import { Plus, X, Printer, Trash2, Pencil, Save, Package, KeyRound, ArrowLeft } from 'lucide-react';

function fmtFecha(iso: string) {
  return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtNum(n: any) { return Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 3 }); }

const ESTADOS: Record<string, { bg: string; txt: string; label: string }> = {
  borrador:  { bg: 'var(--well)', txt: 'var(--muted)', label: 'Borrador' },
  enviada:   { bg: 'var(--info-bg)', txt: 'var(--info)', label: 'Enviada' },
  recibida:  { bg: 'var(--ok-bg)', txt: 'var(--ok)', label: 'Recibida' },
  cancelada: { bg: 'var(--brand-light)', txt: 'var(--brand-texto)', label: 'Cancelada' },
};

// insumo_id = OTRO → artículo libre (limpieza, desechables…), no toca inventario.
const OTRO = '__otro__';
const ITEM_VACIO = { insumo_id: '', nombre: '', unidad: '', cantidad_pedida: '', cantidad_recibida: '', notas: '' };
const itemValido = (i: typeof ITEM_VACIO) => !!i.cantidad_pedida && (i.insumo_id === OTRO ? !!i.nombre.trim() : !!i.insumo_id);

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' };
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', height: 38, border: '1px solid var(--line-strong)', borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const th: React.CSSProperties = { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'left' };
const td: React.CSSProperties = { padding: '10px 16px', fontSize: 13, color: 'var(--ink-2)', borderTop: '1px solid var(--line-2)' };

export default function Requisiciones() {
  const { usuario } = useAuth();
  const puedeProveedores = ['admin', 'almacenista'].includes(usuario?.rol || '');
  const [tab, setTab] = useState<'pedido' | 'recepcion'>('pedido');
  const [lista, setLista] = useState<any[]>([]);
  const [insumos, setInsumos] = useState<any[]>([]);
  const [pedidos, setPedidos] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [seleccionada, setSeleccionada] = useState<any>(null);
  const [modo, setModo] = useState<'lista' | 'nueva' | 'detalle'>('lista');
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  const [formNotas, setFormNotas] = useState('');
  const [formPedidoId, setFormPedidoId] = useState('');
  const [formItems, setFormItems] = useState<typeof ITEM_VACIO[]>([ITEM_VACIO]);
  const [guardando, setGuardando] = useState(false);
  const [llaveReq, setLlaveReq] = useState(false);

  const [editando, setEditando] = useState(false);
  const [itemsEdit, setItemsEdit] = useState<any[]>([]);
  const [guardandoEdit, setGuardandoEdit] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => { cargarLista(); inventarioService.getInsumos().then(r => setInsumos(r.data)); }, [tab]);

  const cargarLista = async () => {
    setCargando(true); setError('');
    try {
      const res = await requisicionesService.getAll({ tipo: tab, limite: 100 });
      setLista(res.data);
      if (tab === 'recepcion') { const p = await requisicionesService.getAll({ tipo: 'pedido', limite: 200 }); setPedidos(p.data); }
    } catch { setError('Error al cargar requisiciones'); }
    finally { setCargando(false); }
  };

  const abrirDetalle = async (id: string) => {
    setError('');
    const res = await requisicionesService.getById(id);
    setSeleccionada(res.data);
    setItemsEdit(res.data.items.map((i: any) => ({ ...i, _recibida: i.cantidad_recibida ?? '' })));
    setEditando(false);
    setModo('detalle');
  };

  const guardar = async () => {
    const items = formItems.filter(itemValido);
    if (!items.length) { setError('Agrega al menos un artículo con cantidad'); return; }
    setGuardando(true); setError('');
    try {
      const payload: any = {
        tipo: tab, notas: formNotas || null,
        items: items.map(i => ({
          ...(i.insumo_id === OTRO ? { nombre: i.nombre.trim(), unidad: i.unidad.trim() || 'pza' } : { insumo_id: i.insumo_id }),
          cantidad_pedida: parseFloat(i.cantidad_pedida),
          cantidad_recibida: tab === 'recepcion' && i.cantidad_recibida ? parseFloat(i.cantidad_recibida) : null,
          notas: i.notas || null,
        })),
      };
      if (tab === 'recepcion' && formPedidoId) payload.pedido_id = formPedidoId;
      await requisicionesService.crear(payload);
      setExito(`Requisición de ${tab} creada`);
      setTimeout(() => setExito(''), 4000);
      setModo('lista'); resetForm(); cargarLista();
    } catch (err: any) { setError(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const resetForm = () => { setFormNotas(''); setFormPedidoId(''); setFormItems([ITEM_VACIO]); };

  const cargarItemsDePedido = async (pedido_id: string) => {
    if (!pedido_id) { setFormItems([ITEM_VACIO]); return; }
    const res = await requisicionesService.getById(pedido_id);
    setFormItems(res.data.items.map((i: any) => ({ ...ITEM_VACIO, ...(i.insumo_id ? { insumo_id: i.insumo_id } : { insumo_id: OTRO, nombre: i.nombre || '', unidad: i.unidad || '' }), cantidad_pedida: String(i.cantidad_pedida), cantidad_recibida: '' })));
  };

  const guardarRecibidas = async () => {
    if (!seleccionada) return;
    setGuardandoEdit(true); setError('');
    try {
      const items = itemsEdit.map(i => ({ id: i.id, cantidad_recibida: i._recibida !== '' ? parseFloat(i._recibida) : null, notas: i.notas }));
      const res = await requisicionesService.actualizar(seleccionada.id, { items });
      setSeleccionada(res.data); setEditando(false);
      setExito('Cantidades actualizadas'); setTimeout(() => setExito(''), 3000);
      cargarLista();
    } catch { setError('Error al guardar'); }
    finally { setGuardandoEdit(false); }
  };

  const aplicarInventario = async () => {
    if (!seleccionada) return;
    if (!window.confirm('¿Aplicar las cantidades recibidas al inventario? Esta acción no se puede deshacer.')) return;
    setAplicando(true); setError('');
    try {
      const res = await requisicionesService.aplicar(seleccionada.id);
      setExito(`Inventario actualizado · ${res.data.aplicados.length} insumo(s) sumados`);
      setTimeout(() => setExito(''), 5000);
      const det = await requisicionesService.getById(seleccionada.id);
      setSeleccionada(det.data); cargarLista();
    } catch (err: any) { setError(err.response?.data?.error || 'Error al aplicar'); }
    finally { setAplicando(false); }
  };

  const eliminar = async (req: any) => {
    const msg = req.estado !== 'borrador' ? `Esta requisición está en estado "${req.estado}". ¿Seguro que quieres eliminarla? Esta acción es irreversible.` : '¿Eliminar esta requisición borrador?';
    if (!window.confirm(msg)) return;
    try { await requisicionesService.eliminar(req.id); if (modo === 'detalle') setModo('lista'); cargarLista(); }
    catch (err: any) { setError(err.response?.data?.error || 'No se pudo eliminar'); }
  };

  const cambiarEstado = async (id: string, estado: string) => {
    try { const res = await requisicionesService.actualizar(id, { estado }); setSeleccionada(res.data); cargarLista(); }
    catch { setError('Error al cambiar estado'); }
  };

  const addItem = () => setFormItems(p => [...p, ITEM_VACIO]);
  const removeItem = (i: number) => setFormItems(p => p.filter((_, n) => n !== i));
  const setItem = (i: number, field: string, val: string) => setFormItems(p => p.map((item, n) => n === i ? { ...item, [field]: val } : item));
  const unidadDeInsumo = (id: string) => insumos.find(i => i.id === id)?.unidad || '';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--text)', margin: 0 }}>Requisiciones</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>Pedido y recepción de insumos y otros artículos</p>
        </div>
        {modo === 'lista' ? (
          <div style={{ display: 'flex', gap: 8 }}>
          {/* Proveedores salió del menú: se entra desde aquí (solo quien los administra). */}
          {puedeProveedores && <Link to="/proveedores" style={{ display: 'flex', alignItems: 'center', height: 36, padding: '0 16px', background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line-strong)', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Proveedores</Link>}
          <button onClick={() => { setModo('nueva'); resetForm(); setError(''); }} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={15} /> Nueva requisición
          </button>
          </div>
        ) : (
          <button onClick={() => { setModo('lista'); setError(''); }} style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: 'var(--brand-texto)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <ArrowLeft size={14} /> Volver a la lista
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--well)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {(['pedido', 'recepcion'] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); setModo('lista'); }}
            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', textTransform: 'capitalize', background: tab === t ? 'var(--surface)' : 'transparent', color: tab === t ? 'var(--brand-texto)' : 'var(--muted)', boxShadow: tab === t ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
            {t === 'pedido' ? 'Pedido' : 'Recepción'}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', color: 'var(--brand-texto)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ border: 'none', background: 'transparent', color: 'var(--brand-texto)', cursor: 'pointer' }}><X size={14} /></button>
        </div>
      )}
      {exito && (
        <div style={{ background: 'var(--ok-bg)', border: '1px solid var(--ok-line)', color: 'var(--ok)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600 }}>{exito}</div>
      )}

      {modo === 'lista' && (
        <div style={{ maxWidth: 900 }}>
          {cargando && <p style={{ color: 'var(--faint)', fontSize: 13 }}>Cargando…</p>}

          {!cargando && tab === 'recepcion' && pedidos.filter(p => p.estado === 'enviada').length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--info)', marginBottom: 8 }}>Pedidos pendientes de recibir</h3>
              {pedidos.filter(p => p.estado === 'enviada').map(p => (
                <div key={p.id} style={{ background: 'var(--info-bg)', border: '1px solid var(--info-line)', borderRadius: 12, padding: '14px 18px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, color: '#1E40AF', fontFamily: 'monospace', fontSize: 13 }}>#{p.id.slice(0, 8).toUpperCase()}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: 'var(--info-bg)', color: 'var(--info)' }}>Enviado</span>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>{fmtFecha(p.created_at)} · {p.usuario_nombre} · {p.num_items} ítem(s)</p>
                    {p.notas && <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0', fontStyle: 'italic' }}>{p.notas}</p>}
                  </div>
                  <button onClick={() => { setModo('nueva'); resetForm(); setFormPedidoId(p.id); cargarItemsDePedido(p.id); }}
                    style={{ flexShrink: 0, background: 'var(--info)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Crear recepción →
                  </button>
                </div>
              ))}
            </div>
          )}

          {!cargando && lista.length === 0 && (
            <div style={{ ...card, padding: 48, textAlign: 'center', color: 'var(--faint)' }}>
              <Package size={32} style={{ marginBottom: 8, opacity: .4 }} />
              <p style={{ margin: 0 }}>Sin requisiciones de {tab}.</p>
              <button onClick={() => { setModo('nueva'); resetForm(); }} style={{ marginTop: 12, border: 'none', background: 'transparent', color: 'var(--brand-texto)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Crear la primera →</button>
            </div>
          )}
          {lista.map(r => {
            const est = ESTADOS[r.estado] || ESTADOS.borrador;
            return (
              <div key={r.id} onClick={() => abrirDetalle(r.id)} style={{ ...card, padding: '14px 18px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>#{r.id.slice(0, 8).toUpperCase()}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: est.bg, color: est.txt }}>{est.label}</span>
                    {r.aplicada_inventario && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: 'var(--ok-bg)', color: 'var(--ok)' }}>En inventario</span>}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>{fmtFecha(r.created_at)} · {r.usuario_nombre} · {r.num_items} ítem(s)</p>
                  {r.notas && <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0', fontStyle: 'italic' }}>{r.notas}</p>}
                </div>
                <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  {/* Se pide el detalle y se imprime ESE resultado. Antes se
                      imprimía la variable `seleccionada` vieja del closure:
                      nada la primera vez, o la requisición equivocada. */}
                  <button onClick={async () => {
                    try { const res = await requisicionesService.getById(r.id); imprimirRequisicion(res.data); }
                    catch { setError('No se pudo cargar la requisición para imprimir'); }
                  }} title="Imprimir PDF" style={{ width: 32, height: 32, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}><Printer size={13} /></button>
                  <button onClick={() => eliminar(r)} title="Eliminar" style={{ width: 32, height: 32, border: '1px solid var(--danger-line)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-texto)' }}><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modo === 'nueva' && (
        <div style={{ maxWidth: 880 }}>
          <div style={{ ...card, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Nueva requisición de {tab === 'pedido' ? 'pedido' : 'recepción'}</h3>

            {tab === 'recepcion' && (
              <div>
                <label style={labelStyle}>Basada en pedido (opcional)</label>
                <select value={formPedidoId} onChange={e => { setFormPedidoId(e.target.value); cargarItemsDePedido(e.target.value); }} style={{ ...inputStyle, background: 'var(--surface)', cursor: 'pointer' }}>
                  <option value="">— Sin pedido base —</option>
                  {pedidos.map(p => <option key={p.id} value={p.id}>#{p.id.slice(0, 8).toUpperCase()} · {fmtFecha(p.created_at)} · {p.usuario_nombre} ({p.num_items} items)</option>)}
                </select>
                {formPedidoId && <p style={{ fontSize: 12, color: 'var(--ok)', margin: '6px 0 0' }}>✓ Items pre-cargados del pedido seleccionado</p>}
              </div>
            )}

            <div>
              <label style={labelStyle}>Notas generales</label>
              <input value={formNotas} onChange={e => setFormNotas(e.target.value)} placeholder="Ej: pedido semana 23, proveedor XYZ…" style={inputStyle} />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={labelStyle}>Artículos</label>
                <button onClick={addItem} style={{ border: 'none', background: 'transparent', color: 'var(--brand-texto)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><Plus size={13} /> Agregar artículo</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {formItems.map((item, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: tab === 'recepcion' ? '2.2fr 1fr 0.6fr 1fr 1.4fr auto' : '2.2fr 1fr 0.6fr 2fr auto', gap: 8, alignItems: 'end', background: 'var(--bg)', borderRadius: 10, padding: 12 }}>
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--faint)', display: 'block', marginBottom: 4 }}>Insumo</label>
                      <select value={item.insumo_id} onChange={e => setItem(i, 'insumo_id', e.target.value)} style={{ ...inputStyle, height: 36, background: 'var(--surface)', cursor: 'pointer' }}>
                        <option value="">Seleccionar…</option>
                        <option value={OTRO}>✎ Otro (limpieza, desechables…)</option>
                        {insumos.map(ins => <option key={ins.id} value={ins.id}>{ins.nombre} ({ins.unidad})</option>)}
                      </select>
                      {item.insumo_id === OTRO && (
                        <input value={item.nombre} onChange={e => setItem(i, 'nombre', e.target.value)} placeholder="Ej: Cloro 1 L" maxLength={150} style={{ ...inputStyle, height: 36, marginTop: 6 }} />
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--faint)', display: 'block', marginBottom: 4 }}>Cant. pedida</label>
                      <input type="number" min="0.001" step="any" readOnly={tab === 'recepcion'} value={item.cantidad_pedida}
                        onChange={tab === 'recepcion' ? undefined : e => setItem(i, 'cantidad_pedida', e.target.value)}
                        style={{ ...inputStyle, height: 36, background: tab === 'recepcion' ? 'var(--well)' : 'var(--surface)', color: tab === 'recepcion' ? 'var(--faint)' : 'var(--ink)' }} placeholder="0" />
                    </div>
                    {item.insumo_id === OTRO
                      ? <input value={item.unidad} onChange={e => setItem(i, 'unidad', e.target.value)} placeholder="pza" maxLength={30} style={{ ...inputStyle, height: 36, padding: '0 6px' }} />
                      : <div style={{ fontSize: 12, color: 'var(--faint)', paddingBottom: 8 }}>{unidadDeInsumo(item.insumo_id)}</div>}
                    {tab === 'recepcion' && (
                      <div>
                        <label style={{ fontSize: 10, color: 'var(--faint)', display: 'block', marginBottom: 4 }}>Cant. recibida</label>
                        <input type="number" min="0" step="any" value={item.cantidad_recibida} onChange={e => setItem(i, 'cantidad_recibida', e.target.value)} style={{ ...inputStyle, height: 36 }} placeholder="0" />
                      </div>
                    )}
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--faint)', display: 'block', marginBottom: 4 }}>Observaciones</label>
                      <input value={item.notas} onChange={e => setItem(i, 'notas', e.target.value)} placeholder="Opcional" style={{ ...inputStyle, height: 36 }} />
                    </div>
                    <button onClick={() => removeItem(i)} disabled={formItems.length === 1} style={{ border: 'none', background: 'transparent', color: 'var(--brand-texto)', cursor: formItems.length === 1 ? 'not-allowed' : 'pointer', opacity: formItems.length === 1 ? .25 : 1, paddingBottom: 8 }}><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setModo('lista'); resetForm(); }} style={{ flex: 1, height: 40, border: '1px solid var(--line-strong)', borderRadius: 8, background: 'var(--surface)', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--ink-2)' }}>Cancelar</button>
              <button onClick={() => { const items = formItems.filter(itemValido); if (!items.length) { setError('Agrega al menos un artículo con cantidad'); return; } setLlaveReq(true); }} disabled={guardando}
                style={{ flex: 2, height: 40, border: 'none', borderRadius: 8, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 13, fontWeight: 600, cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? .7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <KeyRound size={14} /> {guardando ? 'Guardando…' : `Crear requisición de ${tab}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {modo === 'detalle' && seleccionada && (
        <div style={{ maxWidth: 1000 }}>
          <div style={{ ...card, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--line-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <h3 style={{ fontWeight: 700, fontSize: 16, fontFamily: 'monospace', margin: 0 }}>#{seleccionada.id.slice(0, 8).toUpperCase()}</h3>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: ESTADOS[seleccionada.estado]?.bg, color: ESTADOS[seleccionada.estado]?.txt }}>{ESTADOS[seleccionada.estado]?.label}</span>
                  {seleccionada.aplicada_inventario && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: 'var(--ok-bg)', color: 'var(--ok)' }}>En inventario</span>}
                </div>
                <p style={{ fontSize: 13, color: 'var(--faint)', margin: 0 }}>{fmtFecha(seleccionada.created_at)} · {seleccionada.usuario_nombre}{seleccionada.pedido_usuario && ` · Pedido por: ${seleccionada.pedido_usuario}`}</p>
                {seleccionada.notas && <p style={{ fontSize: 13, color: 'var(--muted)', margin: '4px 0 0', fontStyle: 'italic' }}>{seleccionada.notas}</p>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => imprimirRequisicion(seleccionada)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid var(--line-strong)', borderRadius: 8, fontSize: 13, color: 'var(--ink-2)', background: 'var(--surface)', cursor: 'pointer', fontWeight: 500 }}>
                  <Printer size={13} /> Exportar PDF
                </button>
                {seleccionada.estado === 'borrador' && seleccionada.tipo === 'pedido' && (
                  <button onClick={() => cambiarEstado(seleccionada.id, 'enviada')} style={{ padding: '8px 14px', background: 'var(--info)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Marcar enviada</button>
                )}
                <button onClick={() => eliminar(seleccionada)} style={{ padding: '8px 14px', border: '1px solid var(--danger-line)', color: 'var(--brand-texto)', background: 'var(--surface)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Eliminar</button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--bg)' }}>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Artículo</th>
                    <th style={{ ...th, textAlign: 'right' }}>Cant. pedida</th>
                    <th style={{ ...th, textAlign: 'center' }}>Unidad</th>
                    {seleccionada.tipo === 'recepcion' && (<>
                      <th style={{ ...th, textAlign: 'right' }}>Cant. recibida</th>
                      <th style={{ ...th, textAlign: 'right' }}>Diferencia</th>
                    </>)}
                    <th style={th}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(editando ? itemsEdit : seleccionada.items).map((item: any, idx: number) => {
                    const diff = item.cantidad_recibida != null ? Number(item.cantidad_recibida) - Number(item.cantidad_pedida) : null;
                    return (
                      <tr key={item.id}>
                        <td style={{ ...td, color: 'var(--faint)', fontSize: 12 }}>{idx + 1}</td>
                        <td style={{ ...td, fontWeight: 500 }}>{item.insumo_nombre}{!item.insumo_id && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: 'var(--well)', color: 'var(--muted)' }}>Fuera de inventario</span>}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmtNum(item.cantidad_pedida)}</td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>{item.unidad}</td>
                        {seleccionada.tipo === 'recepcion' && (<>
                          <td style={{ ...td, textAlign: 'right' }}>
                            {editando ? (
                              <input type="number" min="0" step="any" value={item._recibida} onChange={e => setItemsEdit(prev => prev.map((it, n) => n === idx ? { ...it, _recibida: e.target.value } : it))}
                                style={{ width: 90, height: 32, border: '1px solid var(--line-strong)', borderRadius: 6, padding: '0 8px', fontSize: 13, textAlign: 'right', outline: 'none' }} />
                            ) : (<span>{item.cantidad_recibida != null ? fmtNum(item.cantidad_recibida) : '—'}</span>)}
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: diff === null ? 'var(--line-strong)' : diff < 0 ? 'var(--brand-texto)' : diff > 0 ? 'var(--ok)' : 'var(--faint)' }}>
                            {diff === null ? '—' : (diff > 0 ? '+' : '') + fmtNum(diff)}
                          </td>
                        </>)}
                        <td style={{ ...td, color: 'var(--faint)', fontSize: 12 }}>{item.notas || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {seleccionada.tipo === 'recepcion' && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--line-2)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                {!seleccionada.aplicada_inventario && (
                  <>
                    {editando ? (
                      <>
                        <button onClick={guardarRecibidas} disabled={guardandoEdit} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: guardandoEdit ? .7 : 1 }}>
                          <Save size={14} /> {guardandoEdit ? 'Guardando…' : 'Guardar cantidades'}
                        </button>
                        <button onClick={() => setEditando(false)} style={{ border: '1px solid var(--line-strong)', color: 'var(--ink-2)', background: 'var(--surface)', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
                      </>
                    ) : (
                      <button onClick={() => setEditando(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--line-strong)', color: 'var(--ink-2)', background: 'var(--surface)', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        <Pencil size={13} /> Editar cantidades recibidas
                      </button>
                    )}
                    <button onClick={aplicarInventario} disabled={aplicando} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--ok)', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: aplicando ? .7 : 1 }}>
                      <Package size={14} /> {aplicando ? 'Aplicando…' : 'Aplicar al inventario'}
                    </button>
                  </>
                )}
                {seleccionada.aplicada_inventario && (
                  <div style={{ color: 'var(--ok)', fontSize: 13, fontWeight: 600 }}>✓ Cantidades ya sumadas al inventario</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {llaveReq && (
        <ModalLlaveAcceso permiso="puede_crear_requisicion" titulo="Crear requisición" descripcion="Esta acción requiere autorización de un usuario con permiso para crear requisiciones."
          onSuccess={() => { setLlaveReq(false); guardar(); }} onCancel={() => setLlaveReq(false)} />
      )}
    </div>
  );
}
